import { useEffect } from "react";
import Image from "next/image";
import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Box,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { MdOutlineContentCopy } from "react-icons/md";
import { HiOutlineExternalLink } from "react-icons/hi";
import { useAccount, useDisconnect } from "wagmi";
import { useCopyToClipboard } from "react-use";

// Types
import Props from "./AccountModal.type";

// Utils
import { shortenAddress } from "utils/common";

// Constants
import { walletIconMapping } from "constants/walletMenuOptions";
import { COPY_ACCOUNT_ADDRESS, DISCONNECT_WALLET } from "constants/analytics";

// Hooks
import useApp from "hooks/useApp";

// Config
import { chainConfig } from "config/chain";

// Components
import AllTransaction from "components/AllTransactions";

const AccountModal = ({
  isOpen,
  txnBatchData,
  handleTxView,
  onClear,
}: Props) => {
  const { handleViewOnEtherScan, closeModal, clearWalletData, isEmbed, track } =
    useApp();
  const { disconnect } = useDisconnect();
  const { address, connector } = useAccount();
  const toast = useToast();

  const [state, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    if (state.value) {
      track(COPY_ACCOUNT_ADDRESS);
      toast({
        description: "Address Copied",
        status: "success",
        duration: 2000,
        position: "top",
        isClosable: true,
      });
    }
  }, [state, track]);

  const handleDisconnect = () => {
    track(DISCONNECT_WALLET);
    disconnect();
    clearWalletData();
    onClear();
    closeModal();
  };

  return (
    <Modal isOpen={isOpen} onClose={closeModal} isCentered>
      <ModalOverlay />
      <ModalContent maxW="450px" bg="brand.backgroundLight" m="0.5rem">
        <ModalHeader fontSize="1rem">Wallet</ModalHeader>
        <ModalCloseButton
          _focus={{
            boxShadow: "none",
          }}
        />
        <ModalBody pt="0" mb="0.5rem">
          <Text fontSize="14px">Connected with {connector?.name}</Text>
          <Flex
            justifyContent="space-between"
            padding="1rem"
            bg="brand.textBg"
            mt="1rem"
            borderRadius="0.5rem"
          >
            <Flex gap="0.5rem">
              <Flex borderRadius="50%" overflow="hidden">
                <Image
                  src={walletIconMapping[connector?.id.toLowerCase() || ""]}
                  width={24}
                  height={24}
                  alt="provider-icon"
                />
              </Flex>
              <Text>{shortenAddress(address || "")}</Text>
            </Flex>
            {!isEmbed && (
              <Box
                color="brand.primary"
                fontFamily="semi-bold"
                cursor="pointer"
                onClick={handleDisconnect}
              >
                Disconnect
              </Box>
            )}
          </Flex>
          <Flex
            justifyContent="space-between"
            padding="1rem 0"
            alignItems="center"
          >
            <Flex
              cursor="pointer"
              color="brand.primary"
              onClick={() => copyToClipboard(address || "")}
              alignItems="center"
              gap="0.5rem"
            >
              <Icon as={MdOutlineContentCopy} />
              <Text fontFamily="medium">Copy Address</Text>
            </Flex>
            {!isEmbed && (
              <Flex
                cursor="pointer"
                color="brand.primary"
                onClick={handleViewOnEtherScan}
                alignItems="center"
                gap="0.5rem"
              >
                <Icon as={HiOutlineExternalLink} />
                <Text fontFamily="medium">
                  View on {chainConfig.blockExplorers?.default.name}
                </Text>
              </Flex>
            )}
          </Flex>
          <AllTransaction
            handleTxView={handleTxView}
            txnBatchData={txnBatchData}
            onClear={onClear}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AccountModal;
